import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useOnceMutation } from '@vnetwork/margarita-shell/once-mutation';
import { Users, User } from 'lucide-react';
import { Button, Input, Checkbox, Skeleton } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { AuthShell, PasswordInput } from './components/AuthShell';
import { PasswordStrengthMeter, scorePassword } from './components/PasswordStrengthMeter';
import { AuthSwitchLink } from './components/AuthSwitchLink';
import { RegisterOtpStep } from './components/RegisterOtpStep';
import authCta from './RegisterPage.module.css';
import { TermsDialog } from './terms/TermsDialog';
import { COMPANY_NAME } from './terms/terms-content';
import { useValidateToken, useAcceptInvitation, useJoinTeam } from '@/api/teams';
import { registerUser } from '@/api/auth-flow';
import { getAuthErrorCode, resolveAuthError } from '@/api/auth-error';
import { tokenStorage } from '@vnetwork/margarita-core/auth';
import { brand, brandCopyright } from '@/config/brand';
import { useAuth } from '@/auth/AuthProvider';

const FOOTER = brandCopyright();

interface FormValues {
  email: string;
  password: string;
  password_confirmation: string;
}

function extractError(e: unknown): string {
  return resolveAuthError(e, 'An unexpected error occurred.');
}

export default function InvitationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? undefined;

  // Check login state via tokenStorage (this page lives outside ProtectedRoute).
  const isLoggedIn = useMemo(() => !!tokenStorage.access && !tokenStorage.isExpired, []);
  const { setSession } = useAuth();

  const { data: validation, isLoading } = useValidateToken(token);
  const acceptMut = useAcceptInvitation();
  const joinMut = useJoinTeam();
  const registerMut = useOnceMutation({ mutationFn: registerUser });

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { email: '', password: '', password_confirmation: '' },
  });

  // Pre-fill email when validation data arrives (if invite targets a specific email).
  useEffect(() => {
    if (validation?.member?.email) {
      reset({ email: validation.member.email, password: '', password_confirmation: '' });
    }
  }, [validation?.member?.email, reset]);

  const [tos, setTos] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  // SSO registration requires an email-verification OTP, so the invite signup is a
  // two-step flow (form → verify email) just like the standalone register page.
  const [step, setStep] = useState<'form' | 'otp'>('form');
  // The submitted form + resolved email, held while the user verifies their email.
  const pendingRef = useRef<{ email: string; password: string; password_confirmation: string } | null>(null);

  const handleAccept = () => {
    setPageError(null);
    acceptMut.mutate(
      { token },
      {
        onSuccess: () => navigate('/teams'),
        onError: (e) => setPageError(extractError(e)),
      },
    );
  };

  // Hold the credentials and move to email verification; the account is created
  // only after the OTP is confirmed (SSO register rejects a missing code).
  const onSubmit = (values: FormValues) => {
    setPageError(null);
    // When the invite targets a fixed address the email field is disabled (RHF omits
    // disabled values), so take the email from the validated invitation instead.
    const email = validation?.member?.email ?? values.email;
    pendingRef.current = {
      email,
      password: values.password,
      password_confirmation: values.password_confirmation,
    };
    setStep('otp');
  };

  // Create the account with the verified OTP, then best-effort join the team.
  // Returns an error message for the OTP step to show, or null on success.
  const completeRegister = async (otp: string): Promise<string | null> => {
    const pending = pendingRef.current;
    if (!pending) return 'Something went wrong. Please go back and try again.';
    try {
      const res = await registerMut.mutateAsync({
        type: 'individual',
        email: pending.email,
        password: pending.password,
        password_confirmation: pending.password_confirmation,
        otp,
      });
      if (res.errors?.length) {
        const m = res.errors[0].message ?? '';
        // Already-registered invitee → send them to sign in (the code/resend form
        // can't help), returning here so the logged-in view offers one-click accept.
        if (/already.?exist/i.test(m)) {
          navigate('/auth/login', {
            state: {
              from: `${location.pathname}${location.search}`,
              email: pending.email,
              notice: 'You already have an account. Please sign in to accept this invitation.',
            },
          });
          return null;
        }
        return m;
      }
      const reg = res.data;
      if (!reg) return 'Registration failed. Please try again.';

      // Best-effort team join; proceed even if it fails (user is registered).
      if (reg.uuid && token) {
        try { await joinMut.mutateAsync({ token, email: pending.email, uid: reg.uuid }); }
        catch { /* continue */ }
      }

      if (reg.access_token && setSession(reg, reg.auth_method ?? 'local')) {
        navigate('/');
      } else {
        navigate('/auth/login');
      }
      return null;
    } catch (e) {
      // Invitee already has an account — send them to sign in, returning to this
      // invite so the logged-in view offers the one-click accept.
      if (getAuthErrorCode(e)?.includes('already-exists')) {
        navigate('/auth/login', {
          state: {
            from: `${location.pathname}${location.search}`,
            email: pending.email,
            notice: 'You already have an account. Please sign in to accept this invitation.',
          },
        });
        return null;
      }
      return extractError(e);
    }
  };

  if (isLoading && token) {
    return (
      <AuthShell title="Team Invitation" footer={FOOTER}>
        <Skeleton style={{ height: 180 }} />
      </AuthShell>
    );
  }

  // Invalid / expired / missing token.
  if (!token || !validation?.valid) {
    return (
      <AuthShell title="Team Invitation" footer={FOOTER}>
        <Alert tone="warning" title="Invalid or expired invitation">
          {validation?.message ||
            'This invitation link is invalid or has expired. Please contact the team owner for a new invite.'}
        </Alert>
        {isLoggedIn && (
          <Button asChild variant="secondary" fullWidth style={{ marginTop: 'var(--vn-space-4)' }}>
            <Link to="/">Return to Dashboard</Link>
          </Button>
        )}
      </AuthShell>
    );
  }

  // Email verification — the account is created only after the OTP is confirmed,
  // so fake/unowned emails can't complete the invite signup.
  if (!isLoggedIn && step === 'otp') {
    return (
      <AuthShell
        eyebrow="verify email"
        title="Verify your email"
        subtitle={pendingRef.current?.email}
        footer={FOOTER}
      >
        <RegisterOtpStep
          email={pendingRef.current?.email ?? ''}
          onVerify={completeRegister}
          submitLabel="Verify & join team"
          onBack={() => {
            setPageError(null);
            setStep('form');
          }}
        />
      </AuthShell>
    );
  }

  // From here: token and validation are both present and valid.
  return (
    <AuthShell
      title="Team Invitation"
      subtitle={
        <>
          You've been invited to join <strong>{validation.team_name}</strong> on the {brand.shortName} Partner
          Console.
        </>
      }
      footer={FOOTER}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {pageError && <Alert tone="danger" title={pageError} />}

        {/* Invitation details summary — a clean bordered card with icon-badged rows. */}
        <div
          style={{
            display: 'grid',
            gap: 'var(--vn-space-4)',
            padding: 'var(--vn-space-4)',
            background: 'var(--vn-bg-surface)',
            border: '1px solid var(--vn-border-default)',
            borderRadius: 'var(--vn-radius-card)',
            boxShadow: 'var(--vn-shadow-sm)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 'var(--vn-radius-control)',
                background: 'var(--vn-brand-subtle)',
                color: 'var(--vn-brand-text)',
              }}
            >
              <Users size={18} />
            </span>
            <div>
              <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Team</div>
              <div style={{ fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-strong)' }}>
                {validation.team_name}
              </div>
            </div>
          </div>
          {validation.owner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 'var(--vn-radius-control)',
                  background: 'var(--vn-brand-subtle)',
                  color: 'var(--vn-brand-text)',
                }}
              >
                <User size={18} />
              </span>
              <div>
                <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Invited by</div>
                <div style={{ fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-strong)' }}>
                  {validation.owner}
                </div>
              </div>
            </div>
          )}
          {/* Only in the logged-in view — the register form below shows the (locked)
              email field itself, so repeating it here would duplicate it. */}
          {isLoggedIn && validation.member?.email && (
            <div
              style={{
                fontSize: 'var(--vn-font-size-sm)',
                padding: 'var(--vn-space-2)',
                background: 'var(--vn-bg-surface)',
                borderRadius: 'var(--vn-radius-sm)',
                border: '1px solid var(--vn-border-subtle)',
              }}
            >
              <span style={{ color: 'var(--vn-fg-muted)' }}>Invited email: </span>
              <strong>{validation.member.email}</strong>
            </div>
          )}
        </div>

        {isLoggedIn ? (
          /* Logged-in: one-click accept */
          <>
            <Button fullWidth size="lg" className={authCta.ctaGradient} disabled={acceptMut.isPending} onClick={handleAccept}>
              {acceptMut.isPending ? 'Accepting…' : 'Accept Invitation'}
            </Button>
            <Button asChild variant="secondary" fullWidth size="lg" className={authCta.tall}>
              <Link to="/">Decline &amp; Return to Dashboard</Link>
            </Button>
          </>
        ) : (
          /* Not logged in: register then join */
          <>
            <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Set your credentials to create an account and join the team.
            </p>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Email" required error={errors.email?.message}>
                {(p) => (
                  <Input
                    {...p}
                    type="email"
                    autoComplete="email"
                    disabled={!!validation.member?.email}
                    {...register('email', { required: 'Email is required.' })}
                  />
                )}
              </Field>
              <Field
                label="Password"
                required
                description="At least 8 characters, with one uppercase letter, one number, and one symbol."
                error={errors.password?.message}
              >
                {(p) => (
                  <>
                    <PasswordInput
                      {...p}
                      autoComplete="new-password"
                      invalid={!!errors.password}
                      {...register('password', {
                        required: 'Password is required.',
                        maxLength: { value: 64, message: 'Password must be at most 64 characters.' },
                        validate: (v) =>
                          scorePassword(v) >= 3 ||
                          'Password does not meet the requirements above.',
                      })}
                    />
                    <PasswordStrengthMeter value={watch('password')} />
                  </>
                )}
              </Field>
              <Field label="Confirm Password" required error={errors.password_confirmation?.message}>
                {(p) => (
                  <PasswordInput
                    {...p}
                    autoComplete="new-password"
                    invalid={!!errors.password_confirmation}
                    {...register('password_confirmation', {
                      required: 'Please confirm your password.',
                      validate: (v) => v === watch('password') || 'Passwords do not match.',
                    })}
                  />
                )}
              </Field>

              <label
                style={{
                  display: 'flex',
                  gap: 'var(--vn-space-2)',
                  alignItems: 'flex-start',
                  fontSize: 'var(--vn-font-size-sm)',
                }}
              >
                <Checkbox
                  checked={tos}
                  onCheckedChange={(v) => {
                    // Agreement is only granted by reading the Terms to the end.
                    if (v) setTermsOpen(true);
                    else setTos(false);
                  }}
                />
                <span>
                  By clicking, you acknowledge and agree to the {COMPANY_NAME}{' '}
                  <button
                    type="button"
                    onClick={() => setTermsOpen(true)}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'var(--vn-primary-text)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Terms of Service
                  </button>
                  .
                </span>
              </label>

              <Button type="submit" fullWidth size="lg" className={authCta.ctaGradient} disabled={!tos}>
                Continue
              </Button>
            </form>

            <TermsDialog
              open={termsOpen}
              onOpenChange={setTermsOpen}
              onAgree={() => {
                setTos(true);
                setTermsOpen(false);
              }}
            />

            {/* Carry the invite so login returns here (logged-in → one-click accept),
                matching the account-already-exists redirect. */}
            <AuthSwitchLink
              prompt="Already have account?"
              action="Sign in"
              to="/auth/login"
              state={{
                from: `${location.pathname}${location.search}`,
                email: validation.member?.email,
              }}
            />
          </>
        )}
      </div>
    </AuthShell>
  );
}
